// Feature-flag catalog. Every flag the code checks is declared here, so the
// server and the agent app agree on the keys and a typo is a type error.
//
// A flag needs no migration: until a super-admin changes it in Admin → Flags it
// has no database row and evaluates to `defaultEnabled` for every account. The
// server does the evaluating (global state, percentage rollout, per-account
// overrides — see server/src/feature-flags/); clients only read the result.
//
// Adding a flag: add an entry below, check it with isFeatureEnabled() /
// requireFeature() on the server and useFeatureFlag() in the agent app.
// Removing one: delete the entry and its checks; leftover rows are ignored.

export interface FeatureFlagDefinition {
  /** Shown in Admin → Flags. */
  description: string;
  /** Value for every account while the flag has no stored state. */
  defaultEnabled: boolean;
}

export const FEATURE_FLAGS = {
  tickets: {
    description: 'Tickets inbox: follow-up work raised from conversations or by hand.',
    defaultEnabled: true,
  },
  flows: {
    description: 'Flows: automations that answer visitors before the AI (questions, branches, API calls).',
    // On for everyone: the flow builder and its templates are part of the
    // chatbot (feedback round). A super-admin can still switch it off here.
    defaultEnabled: true,
  },
} as const satisfies Record<string, FeatureFlagDefinition>;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/** Evaluated flags for one account. */
export type FeatureFlagValues = Record<FeatureFlagKey, boolean>;

export const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];

export function isFeatureFlagKey(key: string): key is FeatureFlagKey {
  return Object.prototype.hasOwnProperty.call(FEATURE_FLAGS, key);
}

/** Every flag at its catalog default — what a client assumes before (or
 *  without) an answer from the server. */
export function defaultFeatureFlagValues(): FeatureFlagValues {
  return Object.fromEntries(
    FEATURE_FLAG_KEYS.map((k) => [k, FEATURE_FLAGS[k].defaultEnabled]),
  ) as FeatureFlagValues;
}
